import { useEffect, useSyncExternalStore } from 'react';
import { simplifyError } from '@/utils/errorMessage';
import { useLocation } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

type DialogOptions = { title: string; confirmLabel?: string; destructive?: boolean };
type Request = DialogOptions & { message: string; confirmation: boolean; resolve: (accepted: boolean) => void; id: number };
let requests: Request[] = [];
let nextId = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const snapshot = () => requests[0] ?? null;

function enqueue(message: string, options: DialogOptions, confirmation: boolean): Promise<boolean> {
  return new Promise(resolve => {
    requests = [...requests, { ...options, message, confirmation, resolve, id: ++nextId }];
    emit();
  });
}

export function confirmDialog(message: string, options: DialogOptions): Promise<boolean> {
  return enqueue(message, options, true);
}

export async function alertDialog(message: string, options: DialogOptions): Promise<void> {
  await enqueue(simplifyError(message), options, false);
}

function dismissAll() {
  const pending = requests;
  requests = [];
  pending.forEach(request => request.resolve(false));
  emit();
}

/** Shared themed dialogs. Requests are queued so simultaneous errors are not lost. */
export default function AppDialogs() {
  const request = useSyncExternalStore(subscribe, snapshot, () => null);
  const location = useLocation();
  // Never run a pending confirmation after navigating away from its screen.
  useEffect(() => dismissAll, [location.key]);
  if (!request) return null;
  const finish = (accepted: boolean) => {
    if (requests[0]?.id !== request.id) return;
    requests = requests.slice(1);
    request.resolve(accepted);
    emit();
  };
  return (
    <Dialog key={request.id} open fullWidth maxWidth="sm" onClose={() => finish(false)}
      aria-labelledby="app-dialog-title" aria-describedby="app-dialog-message">
      <DialogTitle id="app-dialog-title">{request.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="app-dialog-message" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {request.message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {request.confirmation && <Button autoFocus onClick={() => finish(false)}>Cancel</Button>}
        <Button autoFocus={!request.confirmation} variant="contained"
          color={request.destructive ? 'error' : 'primary'} onClick={() => finish(true)}>
          {request.confirmLabel ?? 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
